/**
 * dgCartSync v11 — Distribution Gauthier
 * ───────────────────────────────────────
 * Stratégie LWW (Last Write Wins) avec Session ID :
 *
 *   Chaque onglet/device génère un _sessionId unique à l'init.
 *   Chaque push inclut ce sessionId dans le payload Worker.
 *   Le polling compare le sessionId stocké au Worker vs le nôtre :
 *     • Même sessionId → c'est notre propre push → skip (local = correct)
 *     • sessionId différent → autre device → appliquer le serveur
 *
 *   Cela remplace le mécanisme de cooldown aveugle qui causait
 *   des race conditions dans les deux sens.
 *
 * Flux :
 *   • Page load       → server → local (syncFromServer)
 *   • Cart change     → local → server (syncToServer, debounced 400ms)
 *   • Polling (5s)    → si sessionId ≠ nôtre → appliquer serveur
 *   • Tab visible     → sync immédiate
 *   • Page leave      → best-effort save (keepalive)
 *
 * Config (injecté par theme.liquid quand customer est connecté) :
 *   window.DG_CART_SYNC_CONFIG = { isLoggedIn, customerId, workerUrl, workerSecret }
 */
(function () {
    'use strict';

    var CONFIG = window.DG_CART_SYNC_CONFIG || {};
    var WORKER = (CONFIG.workerUrl || '').replace(/\/+$/, '');
    var SECRET = CONFIG.workerSecret || '';
    var CUSTOMER = CONFIG.customerId || null;
    var IS_LOGGED_IN = !!(CONFIG.isLoggedIn && WORKER && SECRET && CUSTOMER);
    var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    if (!IS_LOGGED_IN) return;
    if (window._dgCartSyncInit) return;
    window._dgCartSyncInit = true;

    /* ─── Redirection post-connexion ─────────────────────────── */
    /* dg-cart-merge.js sauvegarde la page actuelle avant le login.  */
    /* On redirige ici — le merge se fera sur la page destination.   */
    try {
        var _postLoginUrl = localStorage.getItem('dg_post_login_url');
        if (_postLoginUrl && _postLoginUrl !== window.location.href) {
            localStorage.removeItem('dg_post_login_url');
            window.location.href = _postLoginUrl;
            return; /* Le merge s'exécutera sur la page destination */
        } else if (_postLoginUrl) {
            localStorage.removeItem('dg_post_login_url'); /* déjà sur la bonne page */
        }
    } catch (e) { /* localStorage potentiellement bloqué — ignorer */ }

    /* ─── Session ID unique par onglet (LWW) ─────────────────── */
    var _sessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'sid-' + Date.now() + '-' + Math.random().toString(36).slice(2);

    var _syncing = false;
    var _restoring = false;
    var _syncPromise = null;
    var _pushTimer = null;    // debounce syncToServer
    var _cooldownTs = 0;      // rate-limit uniquement (pas de logique de décision)

    console.log('[dgCartSync] v11 LWW — session:', _sessionId.slice(0, 8), '… client:', CUSTOMER);

    /* ─── Worker communication ───────────────────────────────── */
    function workerFetch(method, body) {
        var opts = {
            method: method, credentials: 'omit',
            headers: { 'Content-Type': 'application/json', 'X-CartSyncDG-Key': SECRET }
        };
        var url = WORKER;
        if (method === 'GET') { url += '?customerId=' + CUSTOMER; }
        else { opts.body = JSON.stringify(body); }
        return fetch(url, opts)
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
    }

    /* ─── Lire le panier Shopify ─────────────────────────────── */
    function readSessionCart() {
        return fetch(ROOT + 'cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (c) { return c.items || []; })
            .catch(function () { return []; });
    }

    /* ─── Convertir vers le format Worker ───────────────────── */
    function toWorkerFormat(items) {
        return items.map(function (i) {
            return { variantId: String(i.variant_id || i.id), qty: i.quantity };
        });
    }

    /* ─── Comparer server vs local ──────────────────────────── */
    function isSameCart(serverItems, localItems) {
        if (serverItems.length !== localItems.length) return false;
        var m = {};
        localItems.forEach(function (i) { m[String(i.variant_id || i.id)] = i.quantity; });
        return serverItems.every(function (i) { return m[String(i.variantId)] === parseInt(i.qty, 10); });
    }

    /* ─── Fusionner items server + local (union additive) ───── */
    function mergeCartItems(serverItems, localItems) {
        var merged = {};
        localItems.forEach(function (i) {
            var vid = String(i.variant_id || i.id);
            merged[vid] = (merged[vid] || 0) + i.quantity;
        });
        serverItems.forEach(function (i) {
            var vid = String(i.variantId);
            merged[vid] = (merged[vid] || 0) + parseInt(i.qty, 10);
        });
        return Object.keys(merged).map(function (vid) {
            return { id: parseInt(vid, 10), quantity: merged[vid] };
        });
    }

    /* ─── Appliquer items au panier Shopify (server → local) ─ */
    function applyToSessionCart(serverItems) {
        var toAdd = serverItems
            .filter(function (i) { return i.variantId && parseInt(i.qty, 10) > 0; })
            .map(function (i) { return { id: parseInt(i.variantId, 10), quantity: parseInt(i.qty, 10) }; });

        if (toAdd.length === 0) return Promise.resolve();

        _restoring = true;
        var saved = null;

        return readSessionCart()
            .then(function (cur) {
                saved = cur;
                return fetch(ROOT + 'cart/clear.js', {
                    method: 'POST', credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' }
                });
            })
            .then(function () {
                return fetch(ROOT + 'cart/add.js', {
                    method: 'POST', credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: toAdd })
                });
            })
            .then(function (r) {
                if (!r.ok && saved && saved.length > 0) {
                    // Rollback
                    var rb = saved.map(function (i) { return { id: i.variant_id, quantity: i.quantity }; });
                    return fetch(ROOT + 'cart/add.js', {
                        method: 'POST', credentials: 'same-origin',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items: rb })
                    }).then(function () { throw new Error('rollback'); });
                }
            })
            .then(function () {
                if (typeof window.refreshCartState === 'function') {
                    return new Promise(function (resolve) {
                        document.addEventListener('dg:cart:updated', function h() {
                            document.removeEventListener('dg:cart:updated', h);
                            setTimeout(function () { _restoring = false; }, 150);
                            resolve();
                        });
                        window.refreshCartState();
                        setTimeout(function () { _restoring = false; resolve(); }, 3000);
                    });
                } else { _restoring = false; }
            })
            .catch(function () { _restoring = false; });
    }

    /* ══════════════════════════════════════════════════════════
       PUBLIC API
       ══════════════════════════════════════════════════════════ */
    window.dgCartSync = {

        /**
         * LOCAL → SERVER (debounced 400ms)
         * Inclut le sessionId pour que le polling puisse identifier
         * notre propre push et ne pas écraser l'état local.
         */
        syncToServer: function () {
            clearTimeout(_pushTimer);
            _pushTimer = setTimeout(function () {
                readSessionCart().then(function (items) {
                    var payload = toWorkerFormat(items);
                    console.log('[dgCartSync] ⬆ Push', payload.length, 'article(s) → serveur | session:', _sessionId.slice(0, 8));
                    return workerFetch('POST', {
                        customerId: CUSTOMER,
                        items: payload,
                        sessionId: _sessionId   // ← LWW identifier
                    });
                }).then(function (res) {
                    if (res && res.ok) {
                        console.log('[dgCartSync] ✅ Sauvegardé (' + (res.count || 0) + ')');
                        // Cooldown court uniquement pour éviter une avalanche de requêtes
                        _cooldownTs = Date.now() + 2000;
                    }
                });
            }, 400);
        },

        /**
         * SERVER → LOCAL (page load uniquement)
         * Lit 'dg_cart_merge_action' dans sessionStorage pour appliquer
         * la stratégie choisie par l'utilisateur dans le modal de fusion.
         */
        syncFromServer: function () {
            if (_syncPromise) return _syncPromise;
            _syncing = true;

            /* ── Lire et consommer l'action de fusion ── */
            var mergeAction = null;
            try {
                mergeAction = sessionStorage.getItem('dg_cart_merge_action');
                if (mergeAction) {
                    sessionStorage.removeItem('dg_cart_merge_action');
                    console.log('[dgCartSync] 🔀 Action de fusion détectée:', mergeAction);
                }
            } catch (e) { /* ignore */ }

            _syncPromise = workerFetch('GET')
                .then(function (data) {

                    /* ══ CAS SPÉCIAL : keep_local ══ */
                    if (mergeAction === 'keep_local') {
                        var savedRaw = null;
                        try { savedRaw = sessionStorage.getItem('dg_cart_merge_guest_items'); } catch (e) { }
                        try { sessionStorage.removeItem('dg_cart_merge_guest_items'); } catch (e) { }

                        if (savedRaw) {
                            var savedItems = JSON.parse(savedRaw);
                            if (savedItems && savedItems.length > 0) {
                                console.log('[dgCartSync] 🔀 keep_local: restaurer', savedItems.length, 'article(s)');
                                _restoring = true;
                                return fetch(ROOT + 'cart/clear.js', {
                                    method: 'POST', credentials: 'same-origin',
                                    headers: { 'Content-Type': 'application/json' }
                                }).then(function () {
                                    return fetch(ROOT + 'cart/add.js', {
                                        method: 'POST', credentials: 'same-origin',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ items: savedItems })
                                    });
                                }).then(function () {
                                    _restoring = false;
                                    if (typeof window.refreshCartState === 'function') window.refreshCartState();
                                    return workerFetch('POST', {
                                        customerId: CUSTOMER,
                                        sessionId: _sessionId,
                                        items: savedItems.map(function (i) {
                                            return { variantId: String(i.id), qty: i.quantity };
                                        })
                                    });
                                }).catch(function () { _restoring = false; });
                            }
                        }
                        // Fallback: pousser le panier local actuel
                        return readSessionCart().then(function (local) {
                            _restoring = false;
                            if (typeof window.refreshCartState === 'function') window.refreshCartState();
                            if (local.length > 0) {
                                return workerFetch('POST', {
                                    customerId: CUSTOMER,
                                    sessionId: _sessionId,
                                    items: toWorkerFormat(local)
                                });
                            }
                        });
                    }

                    if (!data || !Array.isArray(data.items)) {
                        return readSessionCart().then(function (local) {
                            if (local.length > 0) {
                                return workerFetch('POST', {
                                    customerId: CUSTOMER,
                                    sessionId: _sessionId,
                                    items: toWorkerFormat(local)
                                });
                            }
                        });
                    }

                    var server = data.items;

                    /* ══ CAS SPÉCIAL : merge ══ */
                    if (mergeAction === 'merge') {
                        var savedGuestRaw = null;
                        try { savedGuestRaw = sessionStorage.getItem('dg_cart_merge_guest_items'); } catch (e) { }
                        try { sessionStorage.removeItem('dg_cart_merge_guest_items'); } catch (e) { }

                        var guestItems = [];
                        if (savedGuestRaw) {
                            try { guestItems = JSON.parse(savedGuestRaw) || []; } catch (e) { }
                        }

                        var guestForMerge = guestItems.map(function (i) {
                            return { variant_id: i.id, quantity: i.quantity };
                        });

                        var serverItems = (server && server.length > 0) ? server : [];
                        console.log('[dgCartSync] 🔀 merge: ' + serverItems.length + ' serveur + ' + guestForMerge.length + ' invité');

                        var merged = mergeCartItems(serverItems, guestForMerge);

                        if (merged.length === 0) {
                            console.log('[dgCartSync] 🔀 merge: paniers vides, rien à faire');
                            return;
                        }

                        var workerItems = merged.map(function (i) {
                            return { variantId: String(i.id), qty: i.quantity };
                        });

                        // ÉTAPE 1 — Sauvegarder sur le Worker EN PREMIER avec notre sessionId
                        _cooldownTs = Date.now() + 8000;
                        return workerFetch('POST', {
                            customerId: CUSTOMER,
                            items: workerItems,
                            sessionId: _sessionId
                        })
                            .then(function (res) {
                                if (res && res.ok) {
                                    console.log('[dgCartSync] ✅ Fusion sauvegardée sur Worker (' + workerItems.length + ' articles)');
                                } else {
                                    console.warn('[dgCartSync] ⚠️ Échec Worker POST pour la fusion');
                                }
                            })
                            .then(function () {
                                _restoring = true;
                                return fetch(ROOT + 'cart/clear.js', {
                                    method: 'POST', credentials: 'same-origin',
                                    headers: { 'Content-Type': 'application/json' }
                                });
                            })
                            .then(function () {
                                return fetch(ROOT + 'cart/add.js', {
                                    method: 'POST', credentials: 'same-origin',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ items: merged })
                                });
                            })
                            .then(function () {
                                _restoring = false;
                                if (typeof window.refreshCartState === 'function') window.refreshCartState();
                                console.log('[dgCartSync] ✅ Panier fusionné appliqué localement');
                            })
                            .catch(function (e) {
                                _restoring = false;
                                console.error('[dgCartSync] Erreur lors de la fusion:', e);
                            });
                    }

                    /* ══ CAS NORMAL (keep_server ou comportement par défaut) ══ */
                    if (server.length === 0) {
                        return readSessionCart().then(function (local) {
                            if (local.length > 0) {
                                console.log('[dgCartSync] 🧹 Serveur vide → vider le panier local');
                                _restoring = true;
                                return fetch(ROOT + 'cart/clear.js', {
                                    method: 'POST', credentials: 'same-origin',
                                    headers: { 'Content-Type': 'application/json' }
                                }).then(function () {
                                    _restoring = false;
                                    if (typeof window.refreshCartState === 'function') window.refreshCartState();
                                }).catch(function () { _restoring = false; });
                            }
                        });
                    }

                    // Server a des items → appliquer si différent
                    return readSessionCart().then(function (local) {
                        if (isSameCart(server, local)) return;
                        return applyToSessionCart(server);
                    });
                })
                .catch(function (e) { console.warn('[dgCartSync] Erreur:', e.message || e); })
                .then(function () {
                    _syncing = false;
                    _syncPromise = null;
                    _cooldownTs = Date.now() + 3000;
                });

            return _syncPromise;
        },

        isSyncEnabled: function () { return IS_LOGGED_IN; }
    };

    /* ─── Auto-init ─────────────────────────────────────────── */
    window.dgCartSync.syncFromServer();

    /* ─── Cart change → push ────────────────────────────────── */
    document.addEventListener('dg:cart:updated', function () {
        if (_restoring) return;
        window.dgCartSync.syncToServer();
    });

    /* ─── Page leave → best-effort save ─────────────────────── */
    function saveOnLeave() {
        if (_syncing || _restoring) return;
        var cart = window.DG_cartItems;
        if (!Array.isArray(cart) || cart.length === 0) return;
        var items = cart.map(function (i) {
            return { variantId: String(i.variant_id || i.id), qty: i.quantity };
        });
        try {
            fetch(WORKER, {
                method: 'POST', credentials: 'omit', keepalive: true,
                headers: { 'Content-Type': 'application/json', 'X-CartSyncDG-Key': SECRET },
                body: JSON.stringify({ customerId: CUSTOMER, items: items, sessionId: _sessionId })
            });
        } catch (e) { /* best effort */ }
    }

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') saveOnLeave();
        if (document.visibilityState === 'visible') setTimeout(pollFromServer, 800);
    });
    window.addEventListener('beforeunload', saveOnLeave);

    /* ─── Polling (5s) — LWW : ignore notre propre push ─────── */
    function pollFromServer() {
        if (_syncing || _restoring) return;
        if (window._dgCartPending) return;
        if (Date.now() < _cooldownTs) return; // rate-limit uniquement

        workerFetch('GET').then(function (data) {
            if (!data || !Array.isArray(data.items)) return;

            /* ══ LWW : si c'est notre propre push → skip ══ */
            if (data.sessionId && data.sessionId === _sessionId) {
                // Ce sont nos propres données — le local est déjà correct
                return;
            }

            /* ══ Autre device ou première connexion → appliquer ══ */
            return readSessionCart().then(function (local) {
                if (data.items.length === 0) {
                    if (local.length > 0) {
                        console.log('[dgCartSync] 🔔 Poll: autre device → serveur vide → vider local');
                        _restoring = true;
                        return fetch(ROOT + 'cart/clear.js', {
                            method: 'POST', credentials: 'same-origin',
                            headers: { 'Content-Type': 'application/json' }
                        }).then(function () {
                            _restoring = false;
                            if (typeof window.refreshCartState === 'function') window.refreshCartState();
                        }).catch(function () { _restoring = false; });
                    }
                    return;
                }

                if (isSameCart(data.items, local)) return;
                console.log('[dgCartSync] 🔔 Poll: delta détecté (autre device) →', data.items.length, 'article(s)');
                return applyToSessionCart(data.items);
            });
        }).catch(function () { });
    }

    setInterval(function () {
        if (document.visibilityState !== 'hidden') pollFromServer();
    }, 5000);

})();
